// Devanāgarī akṣara (syllable) segmentation — the data behind the `order`
// reading exercise ("assemble the word from its syllable tiles"). An akṣara is
// the true orthographic unit of an abugida: an independent vowel, OR a
// consonant cluster (one or more consonants joined by virāma — i.e. a conjunct)
// carrying at most one vowel sign and any following nasal/visarga marks.
//
// The segmenter is deliberately conservative: splitWord returns null whenever
// the result would not round-trip (join === input, NFC), so the engine only
// ever builds an `order` item from a word it can split losslessly. That makes
// the resulting exercise provably sound — the concatenation of the tiles, in
// the authored order, is exactly the original word.

const cp = (s: string, i: number) => s.codePointAt(i) ?? -1;

// Devanāgarī block landmarks (NFC, single code points).
const isIndependentVowel = (c: number) =>
  (c >= 0x0904 && c <= 0x0914) || c === 0x0960 || c === 0x0961 || c === 0x0972; // अ–औ, ॠ, ॡ, ऄ
const isConsonant = (c: number) =>
  (c >= 0x0915 && c <= 0x0939) || (c >= 0x0958 && c <= 0x095f); // क–ह + nukta forms
const isMatra = (c: number) =>
  (c >= 0x093e && c <= 0x094c) || c === 0x0962 || c === 0x0963 || c === 0x093a || c === 0x093b; // vowel signs
const isVirama = (c: number) => c === 0x094d;
const isNukta = (c: number) => c === 0x093c;
const isCombiningMark = (c: number) =>
  c === 0x0901 || c === 0x0902 || c === 0x0903 || c === 0x0951 || c === 0x0952 || c === 0x0953 || c === 0x0954; // candrabindu, anusvāra, visarga, accents
const isOm = (c: number) => c === 0x0950;

/**
 * Split a Devanāgarī word into akṣara tiles. Returns null if the word contains
 * anything unexpected (spaces, digits, daṇḍa, Latin) or fails to round-trip.
 */
export function splitWord(input: string): string[] | null {
  const word = input.normalize('NFC');
  const tiles: string[] = [];
  let i = 0;
  const n = word.length;

  const take = (start: number, end: number) => word.slice(start, end);

  while (i < n) {
    const c = cp(word, i);
    const charLen = c > 0xffff ? 2 : 1;

    if (isOm(c)) {
      tiles.push(take(i, i + charLen));
      i += charLen;
      continue;
    }

    if (isIndependentVowel(c)) {
      let j = i + charLen;
      // an independent vowel may carry a following nasal/visarga
      while (j < n && isCombiningMark(cp(word, j))) j += 1;
      tiles.push(take(i, j));
      i = j;
      continue;
    }

    if (isConsonant(c)) {
      let j = i + charLen;
      // consume nukta on the base consonant
      if (j < n && isNukta(cp(word, j))) j += 1;
      // consume any virāma-joined following consonants (a conjunct stays one tile)
      while (j < n && isVirama(cp(word, j))) {
        const after = cp(word, j + 1);
        if (after >= 0 && isConsonant(after)) {
          j += 1; // the virāma
          j += after > 0xffff ? 2 : 1; // the next consonant
          if (j < n && isNukta(cp(word, j))) j += 1;
        } else {
          // word-final / orphan virāma — keep it attached to this tile
          j += 1;
          break;
        }
      }
      // at most one vowel sign (mātrā)
      if (j < n && isMatra(cp(word, j))) {
        const m = cp(word, j);
        j += m > 0xffff ? 2 : 1;
      }
      // trailing nasal / visarga / accent marks
      while (j < n && isCombiningMark(cp(word, j))) j += 1;
      tiles.push(take(i, j));
      i = j;
      continue;
    }

    // anything else (space, digit, punctuation, Latin) → unsplittable
    return null;
  }

  if (tiles.length === 0) return null;
  if (tiles.join('').normalize('NFC') !== word) return null;
  return tiles;
}
